import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '../../../utils/validation';
import type { IBreadcrumbItem } from '../../elements/common';
import type { IInsurer } from '../../interfaces/insurer';
import type { IService } from '../../interfaces/service';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import {
    insurerServiceAddSchema,
    insurerServiceEditSchema,
} from '../../../../common/validation/insurerServiceDeclarationRule';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    FormContainer,
    Header,
    Loading,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateField } from '../../fields/Date';
import { DropdownField } from '../../fields/Dropdown';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { InsurerServiceDeclarationActivityList } from '../insurerServiceDeclarationActivities/InsurerServiceDeclarationActivityList';
import { InsurerServiceDeclarationSettingList } from '../insurerServiceDeclarationSettings/InsurerServiceDeclarationSettingList';

type IInsurerServiceAddEditProps = IWithRouterProps;

interface IInsurerService {
    exportUntilDate: string | null;
    id?: string;
    insurerId: string;
    serviceId: string;
}

interface IInsurerServiceAddEditState {
    errorCode?: string;
    errors: ValidationErrors;
    insurer?: IInsurer;
    insurers?: IInsurer[];
    insurerServiceDeclarationRule: IInsurerService;
    isSaving: boolean;
    service?: IService;
    services?: IService[];
}

class InnerInsurerServiceAddEdit extends Component<IInsurerServiceAddEditProps, IInsurerServiceAddEditState> {
    constructor(props: IInsurerServiceAddEditProps) {
        super(props);

        this.state = {
            insurerServiceDeclarationRule: {
                insurerId: '',
                serviceId: '',
                exportUntilDate: null,
            },
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        const insurerId = this.props.router.searchParams.get('insurerId');
        const serviceId = this.props.router.searchParams.get('serviceId');
        if (this.props.router.params.id) {
            this.load();
        }
        if (insurerId && !serviceId) {
            this.loadInsurer();
            this.loadServices();
        } else if (!insurerId && serviceId) {
            this.loadService();
            this.loadInsurers();
        } else {
            throw new Error('insurer id and service id are undefined');
        }
    }

    public render() {
        const insurerId = this.props.router.searchParams.getOptional('insurerId');
        const serviceId = this.props.router.searchParams.getOptional('serviceId');
        const returnPage = this.props.router.searchParams.getOptional('returnPage');
        const id = this.props.router.params.id;
        const breadcrumbs: IBreadcrumbItem[] = [
            { text: translateKey('serviceDeclarationRules.insurerServiceDeclarationRule') },
        ];
        if (id) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }
        if (this.state.insurer) {
            breadcrumbs.push({ text: this.state.insurer.name, url: `/insurers/${this.state.insurer.id}` });
        } else if (this.state.service) {
            breadcrumbs.push({ text: this.state.service.name, url: `/services/${this.state.service.id}` });
        }

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header
                    content={
                        id
                            ? translateKey(
                                  'common.editItem',
                                  translateKey('serviceDeclarationRules.insurerServiceDeclarationRule')
                              )
                            : translateKey(
                                  'common.addItem',
                                  translateKey('serviceDeclarationRules.insurerServiceDeclarationRule')
                              )
                    }
                />
                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />
                <Loading
                    item={this.state.insurerServiceDeclarationRule}
                    loaded={(insurerServiceDeclarationRule) => (
                        <>
                            <FormContainer onSubmit={async () => await this.save()}>
                                <FormContextProvider
                                    value={{
                                        placeholderGroupName: '',
                                        item: insurerServiceDeclarationRule,
                                        errors: this.state.errors,
                                        onChange: async (key, value) => await this.handleChange(key, value),
                                        onDoValidate: async (key) => await this.validateOne(key),
                                    }}
                                >
                                    {this.state.services && (
                                        <DropdownField
                                            disabled={!!id}
                                            fieldKey="serviceId"
                                            label={translateKey('service.service')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('service.service')
                                            )}
                                            selectOptions={this.state.services.map((serviceItem) => ({
                                                value: serviceItem.id,
                                                text: serviceItem.name,
                                                optgroup: serviceItem.serviceType,
                                            }))}
                                        />
                                    )}

                                    {this.state.insurers && (
                                        <DropdownField
                                            disabled={!!id}
                                            fieldKey="insurerId"
                                            label={translateKey('insurers.insurer')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('insurers.insurer')
                                            )}
                                            selectOptions={this.state.insurers.map((insurer) => ({
                                                value: insurer.id,
                                                text: insurer.name,
                                            }))}
                                        />
                                    )}

                                    <DateField
                                        fieldKey="exportUntilDate"
                                        label={translateKey('insurers.exportUntilDate')}
                                        placeholder={translateKey('insurers.exportUntilDate')}
                                        size="medium"
                                        tooltipText={translateKey('insurers.exportUntilDateTooltip')}
                                    />
                                </FormContextProvider>

                                <CancelButton
                                    disabled={this.state.isSaving}
                                    to={this.getReturnUrl()}
                                />
                                <SaveSubmit disabled={this.state.isSaving} />
                            </FormContainer>

                            {id && (
                                <InsurerServiceDeclarationSettingList
                                    insurerId={insurerId}
                                    insurerServiceDeclarationRuleId={id}
                                    returnPage={returnPage}
                                    serviceId={serviceId}
                                />
                            )}

                            {id && (
                                <InsurerServiceDeclarationActivityList
                                    insurerId={insurerId}
                                    insurerServiceDeclarationRuleId={id}
                                    returnPage={returnPage}
                                    serviceId={serviceId}
                                />
                            )}
                        </>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurerService>(name: K, value: IInsurerService[K]) {
        await this.setState(({ insurerServiceDeclarationRule }) => ({
            insurerServiceDeclarationRule: { ...insurerServiceDeclarationRule, [name]: value },
        }));
    }

    private async validateOne(name: keyof IInsurerService) {
        await this.setState(({ insurerServiceDeclarationRule, errors }) => ({
            errors: updateValidationErrorsForOne(insurerServiceAddSchema, insurerServiceDeclarationRule, errors, name),
        }));
    }

    private async load() {
        const url = `/api/insurerServiceDeclarationRules/${this.props.router.params.id}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerServiceDeclarationRule: result.json });
    }

    private async loadServices() {
        const url = '/api/services?selectionList=true&sort=serviceType';
        const result = await fetchJson(url, 'GET');
        await this.setState({ services: result.json.items });
    }

    private async loadInsurers() {
        const url = '/api/insurers';
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurers: result.json.items });
    }

    private async loadInsurer() {
        const result = await fetchJson(`/api/insurers/${this.props.router.searchParams.get('insurerId')}`, 'GET');
        await this.setState({ insurer: result.json });
    }

    private async loadService() {
        const result = await fetchJson(`/api/services/${this.props.router.searchParams.get('serviceId')}`, 'GET');
        await this.setState({ service: result.json });
    }

    private async save() {
        let url = '/api/insurerServiceDeclarationRules/';
        const insurerId = this.props.router.searchParams.get('insurerId');
        const serviceId = this.props.router.searchParams.get('serviceId');

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        } else {
            if (serviceId) {
                await this.setState(({ insurerServiceDeclarationRule }) => {
                    return {
                        insurerServiceDeclarationRule: {
                            ...insurerServiceDeclarationRule,
                            serviceId,
                        },
                    };
                });
            }
            if (insurerId) {
                await this.setState(({ insurerServiceDeclarationRule }) => {
                    return {
                        insurerServiceDeclarationRule: {
                            ...insurerServiceDeclarationRule,
                            insurerId,
                        },
                    };
                });
            }
        }

        const { errors, item } = validate(
            this.props.router.params.id ? insurerServiceEditSchema : insurerServiceAddSchema,
            this.state.insurerServiceDeclarationRule
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.DUPLICATE_RECORD]);
        if (result.errorCode) {
            await this.setState({ isSaving: false, errorCode: result.errorCode });
        } else {
            this.props.router.navigate(this.getReturnUrl());
        }
    }

    private getReturnUrl() {
        const insurerId = this.props.router.searchParams.get('insurerId');
        const serviceId = this.props.router.searchParams.get('serviceId');
        const page = this.props.router.searchParams.get('returnPage') ?? '';
        const params = new URLSearchParams({ page });

        if (serviceId) {
            params.set('tab', 'insurerDeclarationRules');

            return `/services/${serviceId}?${params.toString()}`;
        } else if (insurerId) {
            params.set('tab', 'declarationRules');

            return `/insurers/${insurerId}?${params.toString()}`;
        }
        throw new Error('Unknown return destination');
    }
}

export const InsurerServiceAddEdit = withRouter(InnerInsurerServiceAddEdit);
